import { Injectable } from '@angular/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import {
  Dossier,
  Emplacement,
  GlassBehavior,
  GlassDisplayElement,
  LateralDirection,
  LateralPosition,
  LateralPrecision,
  SinisterGlass,
} from '@sc/data-access-models';

@Injectable({
  providedIn: 'root',
})
export class DeclarationConditions {
  treeDisplay: GlassDisplayElement[];
  constructor(protected configurationService: ConfigurationService) {
    this.treeDisplay = configurationService.getGlassUsage()?.treeDisplay;
  }

  getGlassBehavior(sinisterGlassToTest: SinisterGlass): {
    glassBehavior: GlassBehavior;
    index: number;
  } {
    const glassDisplayElementFound = this.treeDisplay?.find(
      (glassDisplayElement) =>
        glassDisplayElement.glass === sinisterGlassToTest?.glass &&
        ((!glassDisplayElement?.glassExtended &&
          !sinisterGlassToTest?.glassExtended) ||
          glassDisplayElement.glassExtended ===
            sinisterGlassToTest.glassExtended)
    );
    if (glassDisplayElementFound?.behavior) {
      return { glassBehavior: glassDisplayElementFound.behavior, index: 0 };
    }
    let subGlassObject;
    switch (sinisterGlassToTest?.glass) {
      case 'windshield':
        subGlassObject = sinisterGlassToTest.impacts;
        break;
      case 'sideWindow':
        subGlassObject = sinisterGlassToTest.lateralPrecisions;
        break;
      case 'lights':
        subGlassObject = sinisterGlassToTest.light;
        break;
      case 'mirror':
        subGlassObject = sinisterGlassToTest;
        break;

      default:
        break;
    }
    for (const question of glassDisplayElementFound?.sub || []) {
      const index = question.values.findIndex(
        (value) =>
          typeof value === 'object' &&
          value.value === subGlassObject?.[question.key]
      );
      const answer = question.values[index] as {
        value: string | boolean;
        behavior?: GlassBehavior;
      };
      if (answer?.behavior) {
        return { glassBehavior: answer.behavior, index: index };
      }
    }
    return { glassBehavior: undefined, index: undefined };
  }

  canBookAppointment(sinisterGlass: SinisterGlass) {
    const { glassBehavior } = this.getGlassBehavior(sinisterGlass);
    switch (glassBehavior) {
      case 'bookableByCallBack':
      case 'bookableByPhone':
      case 'exit':
        return false;
      default:
        return true;
    }
  }

  isCarSet(dossier: Dossier): boolean {
    return (
      !!dossier?.claim?.car?.licensePlateNumber ||
      !!dossier?.claim?.car?.vehicleType ||
      (!!dossier?.claim?.car?.model && !!dossier?.claim?.car?.manufacturer)
    );
  }

  isGlassSet(glass: SinisterGlass): boolean {
    return !!glass?.glass;
  }

  isGlassWindshield(glass: SinisterGlass): boolean {
    return glass?.glass === 'windshield';
  }

  isGlassRearWindow(glass: SinisterGlass): boolean {
    return glass?.glass === 'rearWindow';
  }

  isGlassTopWindow(glass: SinisterGlass): boolean {
    return glass?.glass === 'topWindow';
  }

  isGlassMirrors(
    glass: SinisterGlass,
    options?: { lateral?: LateralPosition } // set an option to null in order to test falsy on glass
  ): boolean {
    return (
      glass?.glass === 'mirror' &&
      // Test lateral option
      ((options?.lateral === null && !glass?.mirror) ||
        options?.lateral === undefined ||
        glass?.mirror === options?.lateral)
    );
  }

  isGlassSideWindow(
    glass: SinisterGlass,
    options?: { precision?: LateralPrecision; direction?: LateralDirection } // set an option to null in order to test falsy on glass
  ): boolean {
    return (
      glass?.glass === 'sideWindow' &&
      // Test lateral precision
      ((options?.precision === null && !glass?.lateralPrecisions?.precision) ||
        options?.precision === undefined ||
        glass?.lateralPrecisions?.precision === options?.precision) &&
      // Test lateral direction
      ((options?.direction === null &&
        !glass?.lateralPrecisions?.frontConductor) ||
        options?.direction === undefined ||
        glass?.lateralPrecisions?.frontConductor === options?.direction)
    );
  }

  isGlassLights(
    glass: SinisterGlass,
    options?: { lateral?: LateralPosition; emplacement?: Emplacement } // set an option to null in order to test falsy on glass
  ): boolean {
    return (
      glass?.glass === 'lights' &&
      // Test lateral option
      ((options?.lateral === null && !glass?.light?.lateral) ||
        options?.lateral === undefined ||
        glass?.light?.lateral === options?.lateral) &&
      // Test emplacement option
      ((options?.emplacement === null && !glass?.light?.lightsEmplacement) ||
        options?.emplacement === undefined ||
        glass?.light?.lightsEmplacement === options?.emplacement)
    );
  }

  isGlassCustode(glass: SinisterGlass): boolean {
    return (
      glass?.glass === 'sideWindow' &&
      (glass?.lateralPrecisions?.precision === 'custodeFrontGlass' ||
        glass?.lateralPrecisions?.precision === 'custodeGlass' ||
        glass?.lateralPrecisions?.precision === 'anotherCustode' ||
        glass?.lateralPrecisions?.precision === 'anyCustode')
    );
  }

  isGlassClassicLateral(glass: SinisterGlass): boolean {
    return (
      glass?.glass === 'sideWindow' &&
      (glass?.lateralPrecisions?.precision === 'conductorGlass' ||
        glass?.lateralPrecisions?.precision === 'anotherGlass' ||
        glass?.lateralPrecisions?.precision === 'anyLateral')
    );
  }

  isGlassMultiple(glass: SinisterGlass) {
    return glass?.glass === 'multiple';
  }

  isGlassUniqueChoice(glass: SinisterGlass): boolean {
    return (
      this.isGlassSet(glass) &&
      !this.isGlassWindshield(glass) &&
      !this.isGlassMirrors(glass) &&
      !this.isGlassSideWindow(glass) &&
      !this.isGlassLights(glass)
    );
  }

  hasWindshieldImpactType(glass: SinisterGlass): boolean {
    return this.isGlassWindshield(glass) && !!glass?.impacts?.impactsType;
  }

  isRepairableWindshieldImpactType(glass: SinisterGlass): boolean {
    return (
      this.isGlassWindshield(glass) &&
      (glass?.impacts?.impactsType === '1impact' ||
        glass?.impacts?.impactsType === '2impactsLess' ||
        glass?.impacts?.impactsType === '3impactsLess' ||
        glass?.impacts?.impactsType === '2or3impacts')
    );
  }

  hasWindshieldImpactSize(glass: SinisterGlass): boolean {
    return this.hasWindshieldImpactType(glass) && !!glass?.impacts?.size;
  }

  isRepairableWindshieldImpactSize(glass: SinisterGlass): boolean {
    return (
      this.isRepairableWindshieldImpactType(glass) &&
      glass?.impacts?.size === 'smaller2cm'
    );
  }

  hasWindshieldImpactLocation(glass: SinisterGlass): boolean {
    return (
      this.hasWindshieldImpactSize(glass) &&
      ((glass?.impacts?.inVisionField !== null &&
        glass?.impacts?.inVisionField !== undefined) ||
        (glass?.impacts?.atOuterEdge !== null &&
          glass?.impacts?.atOuterEdge !== undefined))
    );
  }

  isRepairableWindshield(glass: SinisterGlass): boolean {
    return (
      this.isRepairableWindshieldImpactSize(glass) &&
      this.hasWindshieldImpactLocation(glass) &&
      !glass?.impacts?.inVisionField &&
      !glass?.impacts?.atOuterEdge
    );
  }

  isUnrepairableWindshiled(glass: SinisterGlass): boolean {
    return (
      this.isGlassWindshield(glass) &&
      ((this.hasWindshieldImpactType(glass) &&
        !this.isRepairableWindshieldImpactType(glass)) ||
        (this.hasWindshieldImpactSize(glass) &&
          !this.isRepairableWindshieldImpactSize(glass)) ||
        (this.hasWindshieldImpactLocation(glass) &&
          !this.isRepairableWindshield(glass)))
    );
  }

  hasMirrorsLocation(glass: SinisterGlass): boolean {
    return this.isGlassMirrors(glass) && !!glass?.mirror;
  }

  hasSideWindowLocation(glass: SinisterGlass): boolean {
    return (
      this.isGlassSideWindow(glass) &&
      !!glass?.lateralPrecisions?.frontConductor &&
      !!glass?.lateralPrecisions?.precision &&
      glass?.lateralPrecisions?.precision !== 'anyLateral' &&
      glass?.lateralPrecisions?.precision !== 'anyCustode' &&
      glass?.lateralPrecisions?.precision !== 'all'
    );
  }

  hasLightsLocation(glass: SinisterGlass): boolean {
    return (
      this.isGlassLights(glass) &&
      !!glass?.light?.lateral &&
      !!glass?.light?.lightsEmplacement
    );
  }

  isGlassInfosFilled(dossier: Dossier): boolean {
    return this.isCarSet(dossier) && this.isGlassChoiceFilled(dossier);
  }

  isGlassChoiceFilled(dossier: Dossier): boolean {
    return (
      this.isGlassUniqueChoice(dossier?.claim?.glass) ||
      this.isRepairableWindshield(dossier?.claim?.glass) ||
      this.isUnrepairableWindshiled(dossier?.claim?.glass) ||
      this.hasLightsLocation(dossier?.claim?.glass) ||
      this.hasMirrorsLocation(dossier?.claim?.glass) ||
      this.hasSideWindowLocation(dossier?.claim?.glass)
    );
  }

  hasLicensePlate(dossier: Dossier) {
    return this.isCarSet(dossier) && !!dossier?.claim?.car?.licensePlateNumber;
  }

  hasDisplayableCarInformations(dossier: Dossier) {
    return (
      this.hasLicensePlate(dossier) &&
      ((!!dossier?.claim?.car?.manufacturer && !!dossier?.claim?.car?.model) ||
        dossier?.claim?.car?.countryCode !== 'fr')
    );
  }

  hasValidAppointment(dossier: Dossier) {
    return (
      !!dossier?.appointment &&
      !!dossier?.appointment?.id &&
      !!dossier?.appointment?.time &&
      !!dossier?.appointment?.fitterId &&
      !!dossier?.appointment?.interventionLocation
    );
  }

  hasBeenBookedOrRequested(dossier: Dossier): boolean {
    return (
      (!!dossier?.appointment?.status &&
        dossier?.appointment?.status !== 'MODIFIED' &&
        dossier?.appointment?.status !== 'CANCELLED') ||
      dossier?.requestAppointment?.status === 'REQUESTED'
    );
  }

  hasNoStatusAppointment(dossier: Dossier) {
    return (
      dossier?.appointment?.interventionLocation &&
      dossier?.appointment?.id &&
      !dossier?.appointment?.status
    );
  }

  isDossierComplete(dossier: Dossier) {
    return (
      dossier?.claim?.car &&
      dossier?.claim?.glass?.glass &&
      (dossier?.appointment?.id || dossier?.requestAppointment?.status) &&
      (dossier?.state === 'COMPLETE' ||
        dossier?.state === 'DONE' ||
        dossier?.state === 'CANCELLED')
    );
  }

  hasRequestedAppointment(dossier: Dossier) {
    return (
      !dossier?.appointment?.id &&
      !!(
        dossier?.requestAppointment?.brandId ||
        dossier?.requestAppointment?.status === 'REQUESTED'
      )
    );
  }

  isCarTypeCallbackType(dossier: Dossier) {
    return (
      !!dossier?.claim?.car?.vehicleType &&
      dossier?.claim?.car?.vehicleType !== 'car' &&
      dossier?.claim?.car?.vehicleType !== 'utility'
    );
  }

  isGlassCallbackType(dossier: Dossier) {
    return (
      this.getGlassBehavior(dossier?.claim?.glass)?.glassBehavior ===
      'bookableByCallBack'
    );
  }

  isGlassDeclarationEndType(dossier: Dossier) {
    return (
      this.getGlassBehavior(dossier?.claim?.glass)?.glassBehavior ===
      'declarationEnd'
    );
  }

  isDossierCallbackType(dossier: Dossier) {
    return (
      this.isCarTypeCallbackType(dossier) || this.isGlassCallbackType(dossier)
    );
  }

  isAppointmentModified(dossier: Dossier) {
    return (
      dossier?.appointment?.status === 'MODIFIED' ||
      dossier?.requestAppointment?.status === 'MODIFIED' ||
      dossier?.previousAppointments?.length > 0
    );
  }

  isAppointmentCancelled(dossier: Dossier) {
    return (
      dossier?.appointment?.status === 'CANCELLED' ||
      dossier?.requestAppointment?.status === 'CANCELLED' ||
      dossier?.state === 'CANCELLED'
    );
  }

  isCustomerInfosFilled(dossier: Dossier) {
    return (
      !!dossier?.customer?.firstname &&
      !!dossier?.customer?.lastname &&
      !!dossier?.customer?.email &&
      !!dossier?.customer?.phoneNumber
    );
  }

  canEditB2BDossierAfterApiCreation(dossier: Dossier) {
    return dossier?.policyInformation?.sinisterNumber
      ? !this.configurationService
          .getInsurerFeatures()
          ?.includes('b2b-disallow-api-creation-dossier-edit')
      : true;
  }

  canEditB2CDossierAfterApiCreation(dossier: Dossier) {
    return dossier?.policyInformation?.sinisterNumber
      ? !this.configurationService
          .getInsurerFeatures()
          ?.includes('b2c-disallow-api-creation-dossier-edit')
      : true;
  }
}
